import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentApi, settingsApiFull } from '../api';
import { Btn, Input, Alert } from '../components/ui';

const STEPS = ['Work Calendar', 'Departments', 'Settings', 'Done'];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', marginBottom: '32px' }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '13px', fontWeight: 700,
              background: i < current ? 'var(--color-success)'
                        : i === current ? 'var(--color-primary)' : 'var(--color-border)',
              color: i <= current ? '#fff' : 'var(--color-text-muted)',
              transition: 'background 0.3s',
            }}>
              {i < current ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: '11px', color: i === current ? 'var(--color-primary)' : 'var(--color-text-muted)', fontWeight: i === current ? 600 : 400 }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              width: '48px', height: '2px', marginBottom: '18px',
              background: i < current ? 'var(--color-success)' : 'var(--color-border)',
              transition: 'background 0.3s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Step 1: Work Calendar ─────────────────────────────── */
function StepCalendar({ settings, setSettings, onNext }) {
  return (
    <div>
      <h2 style={S.stepTitle}>Work Calendar</h2>
      <p style={S.stepSubtitle}>Set your company work week. This affects deadline calculations.</p>

      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <label style={S.label}>Work week</label>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            {[5, 6].map(days => (
              <button key={days} onClick={() => setSettings(s => ({ ...s, workDaysPerWeek: days }))} style={{
                ...S.optionBtn,
                background: settings.workDaysPerWeek === days ? 'var(--color-primary)' : 'var(--color-surface)',
                color:      settings.workDaysPerWeek === days ? '#fff' : 'var(--color-text)',
                border:     `2px solid ${settings.workDaysPerWeek === days ? 'var(--color-primary)' : 'var(--color-border)'}`,
              }}>
                {days} days/week
                <span style={{ display: 'block', fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                  {days === 5 ? 'Mon – Fri' : 'Mon – Sat'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={S.label}>Company Holidays</label>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '4px', marginBottom: '8px' }}>
            You can add holidays later in System Settings.
          </p>
          <HolidayInput
            holidays={settings.holidays}
            onChange={h => setSettings(s => ({ ...s, holidays: h }))}
          />
        </div>
      </div>

      <div style={S.actions}>
        <Btn onClick={onNext} size="lg">Continue</Btn>
      </div>
    </div>
  );
}

function HolidayInput({ holidays, onChange }) {
  const [newDate, setNewDate] = useState('');
  const add = () => {
    if (!newDate || holidays.includes(newDate)) return;
    onChange([...holidays, newDate].sort());
    setNewDate('');
  };
  const remove = (d) => onChange(holidays.filter(h => h !== d));
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px' }} />
        <Btn onClick={add} variant="secondary" size="sm">Add</Btn>
      </div>
      {holidays.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {holidays.map(d => (
            <span key={d} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', borderRadius: '999px', fontSize: '12px',
              background: 'var(--color-primary-lighter)', color: 'var(--color-primary)',
            }}>
              {d}
              <button onClick={() => remove(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Step 2: Departments ───────────────────────────────── */
function StepDepartments({ departments, setDepartments, onNext, onBack }) {
  const [name, setName]   = useState('');
  const [error, setError] = useState('');
  const [loading, setLoad] = useState(false);

  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (departments.find(d => d.name.toLowerCase() === trimmed.toLowerCase())) {
      setError('Department already added'); return;
    }
    setError(''); setLoad(true);
    try {
      const res = await departmentApi.create(trimmed);
      if (res.data.success) {
        setDepartments(prev => [...prev, res.data.data]);
        setName('');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create department');
    } finally {
      setLoad(false);
    }
  };

  const remove = async (id) => {
    try {
      await departmentApi.delete(id);
      setDepartments(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError(e.response?.data?.message || 'Cannot delete department');
    }
  };

  return (
    <div>
      <h2 style={S.stepTitle}>Create Departments</h2>
      <p style={S.stepSubtitle}>Add at least one department to get started. You can add more later.</p>
      <div style={{ marginTop: '24px' }}>
        <Alert type="error" message={error} />
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <input
            placeholder="e.g. Engineering, Sales, Operations"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            style={{ flex: 1, padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '15px' }}
          />
          <Btn onClick={add} loading={loading} variant="secondary">Add</Btn>
        </div>
        {departments.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {departments.map(d => (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: 'var(--color-surface)',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>🏢</span>
                  <span style={{ fontWeight: 500 }}>{d.name}</span>
                </div>
                <button onClick={() => remove(d.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-danger)', fontSize: '18px', padding: '4px',
                }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={S.actions}>
        <Btn variant="ghost" onClick={onBack}>Back</Btn>
        <Btn onClick={onNext} disabled={departments.length === 0}>Continue</Btn>
      </div>
    </div>
  );
}

/* ── Step 3: Settings ──────────────────────────────────── */
function StepSettings({ settings, setSettings, onNext, onBack }) {
  return (
    <div>
      <h2 style={S.stepTitle}>System Settings</h2>
      <p style={S.stepSubtitle}>Configure defaults. All of these can be changed later in Settings.</p>
      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Input
          label="Default password for new employees"
          value={settings.defaultPassword}
          onChange={e => setSettings(s => ({ ...s, defaultPassword: e.target.value }))}
          placeholder="Tracern@123"
        />
        <div>
          <label style={S.label}>Session timeout (minutes)</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {[30, 60, 120].map(v => (
              <button key={v} onClick={() => setSettings(s => ({ ...s, sessionTimeoutMinutes: v }))} style={{
                ...S.optionBtn, flex: 1,
                background: settings.sessionTimeoutMinutes === v ? 'var(--color-primary)' : 'var(--color-surface)',
                color:      settings.sessionTimeoutMinutes === v ? '#fff' : 'var(--color-text)',
                border:     `2px solid ${settings.sessionTimeoutMinutes === v ? 'var(--color-primary)' : 'var(--color-border)'}`,
              }}>{v} min</button>
            ))}
          </div>
        </div>
        <div>
          <label style={S.label}>On-hold escalation threshold (working hours)</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {[8, 24, 48].map(v => (
              <button key={v} onClick={() => setSettings(s => ({ ...s, onHoldThresholdHours: v }))} style={{
                ...S.optionBtn, flex: 1,
                background: settings.onHoldThresholdHours === v ? 'var(--color-primary)' : 'var(--color-surface)',
                color:      settings.onHoldThresholdHours === v ? '#fff' : 'var(--color-text)',
                border:     `2px solid ${settings.onHoldThresholdHours === v ? 'var(--color-primary)' : 'var(--color-border)'}`,
              }}>{v}h</button>
            ))}
          </div>
        </div>
        <Input
          label="Daily digest time (24h format)"
          type="time"
          value={settings.digestTime}
          onChange={e => setSettings(s => ({ ...s, digestTime: e.target.value }))}
        />
      </div>
      <div style={S.actions}>
        <Btn variant="ghost" onClick={onBack}>Back</Btn>
        <Btn onClick={onNext}>Finish Setup</Btn>
      </div>
    </div>
  );
}

/* ── Step 4: Done ──────────────────────────────────────── */
function StepDone({ onGo }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
      <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-primary)', marginBottom: '8px' }}>
        Tracern is ready
      </h2>
      <p style={{ fontSize: '15px', color: 'var(--color-text-muted)', lineHeight: 1.6, marginBottom: '32px' }}>
        Your company is set up. Start by creating employees and assigning them to departments.
      </p>
      <Btn onClick={onGo} size="lg">Go to Dashboard</Btn>
    </div>
  );
}

/* ── Main wizard ───────────────────────────────────────── */
export default function SetupWizard() {
  const navigate    = useNavigate();
  const [step, setStep] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    workDaysPerWeek:      5,
    holidays:             [],
    defaultPassword:      'Tracern@123',
    sessionTimeoutMinutes:60,
    onHoldThresholdHours: 24,
    digestTime:           '09:00',
  });

  const saveAndNext = async () => {
    setError('');
    try {
      await settingsApiFull.update({ ...settings, setupComplete: step === 2 });
      setStep(s => s + 1);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save settings');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
      background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)',
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px', padding: '40px 36px',
        width: '100%', maxWidth: '520px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--color-primary)', color: '#fff', fontSize: '22px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>K</div>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>Company Setup</p>
        </div>
        <StepIndicator current={step} />
        {error && <Alert type="error" message={error} />}
        {step === 0 && <StepCalendar    settings={settings} setSettings={setSettings} onNext={saveAndNext} />}
        {step === 1 && <StepDepartments departments={departments} setDepartments={setDepartments} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
        {step === 2 && <StepSettings    settings={settings} setSettings={setSettings} onNext={saveAndNext} onBack={() => setStep(1)} />}
        {step === 3 && <StepDone        onGo={() => navigate('/dashboard/super-admin')} />}
      </div>
    </div>
  );
}

const S = {
  stepTitle:   { fontSize: '20px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '6px' },
  stepSubtitle:{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.5 },
  label:       { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
  actions:     { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '32px' },
  optionBtn: {
    padding: '12px 16px', borderRadius: 'var(--radius-md)',
    cursor: 'pointer', fontSize: '14px', fontWeight: 500,
    transition: 'all 0.15s', textAlign: 'center',
  },
};
